import { useTranslation } from "react-i18next";
import { IDashboardStats } from "../../models/DashboardStats";
import DashboardTile from "./DashboardTile";

interface IVehicleDismantlingListsTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

// Every key below is a real field on IDashboardStats, so this is typed against it directly. It
// used to widen the type with an all-optional `IDashboardStats & { ... }` cast, which meant a
// mistyped key compiled fine and rendered an em-dash - the same trap that left VehicleProcessing
// and MaterialsRecovered silently empty.
const rowConfig: {
  key: keyof IDashboardStats;
  label: string;
  bgClass: string;
  textClass: string;
}[] = [
  // Vehicles in process: on site, awaiting dismantling, or being dismantled. Counted live from
  // Vehicle.Status - see VehicleInventoryRules. This row was hard-coded to "—" via a `noData`
  // flag while the API returned a placeholder zero; both are now real.
  { key: "totalVehiclesOnsite", label: "Total Number Vehicles Onsite", bgClass: "bg-slate-50", textClass: "text-slate-700" },
  { key: "vehiclesAwaitingDismantlingLists", label: "Collected Vehicles Awaiting Jobs", bgClass: "bg-amber-50", textClass: "text-amber-700" },
  { key: "vehiclesWithDismantlingLists", label: "Vehicles With Unscheduled Jobs", bgClass: "bg-green-50", textClass: "text-green-700" },
  { key: "totalPartsAwaitingRemoval", label: "Parts Awaiting Removal", bgClass: "bg-sky-50", textClass: "text-sky-700" },
  { key: "totalEnginesAwaitingRemoval", label: "Engines Awaiting Removal", bgClass: "bg-fuchsia-50", textClass: "text-fuchsia-700" },
];

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

export default function VehicleDismantlingListsTable({ dashboardStats, span = 2 }: IVehicleDismantlingListsTableProps) {
  const { t } = useTranslation("dashboard");

  if (!dashboardStats) return null;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">{t("Vehicle Dismantling Lists")}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{t("Unscheduled jobs status at a glance")}</p>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Metric")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Value")}</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map(({ key, label, bgClass, textClass }) => {
                const value = dashboardStats[key] as number | null | undefined;
                return (
                  <tr key={String(key)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{t(label)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
